/**
 * Staff URLs for appearance-outcome alerts, plus a remap of the paths the cron used to write.
 *
 * Live staff pages are `/ticket?Ticket_guid=` and `/calendar`. Older alerts still point at
 * `/manager/case/...` and `/manager/calendar?...`, which 404. Remap those before navigate.
 */

export function staffTicketUrl(ticketGuid: string, courtEventGuid?: string | null): string {
  const params = new URLSearchParams();
  params.set('Ticket_guid', ticketGuid);
  if (courtEventGuid) params.set('courtEvent', courtEventGuid);
  return `/ticket?${params.toString()}`;
}

export function staffCalendarAwaitingUrl(): string {
  return '/calendar?appearanceOutcome=awaiting';
}

/**
 * Is this one of `AppearanceOutcomeCron`'s failure-to-appear alerts?
 *
 * Matched on the title because that is the only thing that separates the two alerts the cron
 * writes: the failure ones are per case and deep-link to a setting, the "no appearance recorded"
 * summary points at the calendar. Only the first is worth a "needs cover?" action — a new setting
 * follows a failure to appear, and somebody has to stand up at it.
 */
export function isFailureToAppearAlert(title: string | null | undefined): boolean {
  return (title ?? '').startsWith('Failure to appear');
}

/**
 * Turn a stored alert URL into a path the manager router actually serves.
 * Unknown URLs are returned unchanged.
 */
export function remapAppearanceAlertUrl(url: string): string {
  if (!url) return url;

  const calendarMatch = url.match(/^\/manager\/calendar(?:\?(.*))?$/);
  if (calendarMatch) {
    const params = new URLSearchParams(calendarMatch[1] || '');
    if (!params.get('appearanceOutcome')) {
      params.set('appearanceOutcome', 'awaiting');
    }
    const query = params.toString();
    return query ? `/calendar?${query}` : '/calendar';
  }

  const caseMatch = url.match(/^\/manager\/case\/([^/?#]+)(?:\?(.*))?$/);
  if (caseMatch) {
    const ticketGuid = decodeURIComponent(caseMatch[1]);
    const params = new URLSearchParams(caseMatch[2] || '');
    return staffTicketUrl(ticketGuid, params.get('courtEvent'));
  }

  return url;
}
